import { AppTheme } from './AppTheme'
import { Entrance } from './entrance/Entrance'

import { Button, Card, Typography, makeStyles } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { BrowserRouter, Redirect, Route, Switch, useRouteMatch } from 'react-router-dom'
import { SWRConfig } from 'swr'
import { useServerTimeOffset } from './api'
import { formatDate, formatDuration } from './format-helpers'
import { Side } from './side/Side'
import { VerifyUser } from './side/VerifyUser'
import { signedIn } from './store/slices/auth-slice'
import { store, useRootSelector } from './store/store'
import { strings } from './strings'
// import { LogViewer } from './log-viewer/LogViewer'
import Map from 'Map'
import { Provider, useDispatch } from 'react-redux'
import MapProvider from 'Map/context/MapProvider'

export default function App() {
  return (
    <Provider store={store}>
      <AppTheme>
        <BrowserRouter basename="/panel">
          <SWRConfig
            value={{
              shouldRetryOnError: false,
              revalidateOnFocus: false,
            }}
          >
            <RouteForApp />
            <AuthForApp />
          </SWRConfig>
        </BrowserRouter>
      </AppTheme>
    </Provider>
  )
}

function TimeGate(props: any) {
  const offsetState = useServerTimeOffset()
  const classes = useTimeGateStyles()
  const [pass, setPass] = useState(() => sessionStorage.getItem('time-gate-pass') || 'false')

  if (offsetState) {
    const [offset, serverDate, clientDate] = offsetState
    if (pass === 'false' && offset > 10000) {
      return (
        <Card className={classes.root}>
          <Typography>{strings.invalidTimeError}</Typography>
          <Typography>
            {strings.serverTime}:{' '}
            <span style={{ unicodeBidi: 'plaintext' }}>{formatDate(serverDate)}</span>
          </Typography>
          <Typography>
            {strings.yourTime}:{' '}
            <span style={{ unicodeBidi: 'plaintext' }}>{formatDate(clientDate)}</span>
          </Typography>
          <Typography>
            {strings.difference}: {formatDuration(offset / 1000)}
          </Typography>
          <Button
            onClick={() => {
              sessionStorage.setItem('time-gate-pass', 'true')
              setPass('true')
            }}
            variant="outlined"
            color="secondary"
            className={classes.goInButton}
          >
            {strings.goInPanel}
          </Button>
        </Card>
      )
    }
  }

  return props.children
}

const useTimeGateStyles = makeStyles((theme) => ({
  root: {
    maxWidth: 400,
    margin: theme.spacing(2, 'auto'),
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  goInButton: {
    marginTop: theme.spacing(2),
    maxWidth: 100,
  },
}))

function RouteForApp() {
  return (
    <Switch>
      <Route path="/auth" component={Entrance} />
      <Route path="/verify" component={VerifyUser} />
      <Route>
        <TimeGate>
          <MapProvider>
            <Side />
            <Map />
          </MapProvider>
        </TimeGate>
      </Route>
    </Switch>
  )
}

function AuthForApp() {
  const match = useRouteMatch('/auth')
  const { userId } = useToken()
  const [loaded, setLoaded] = useState(false)
  const dispatch = useDispatch()

  useEffect(() => {
    const token = localStorage.getItem('auth-token') || sessionStorage.getItem('auth-token')
    if (token) {
      dispatch(signedIn(token))
    }
    setLoaded(true)
  }, [dispatch])

  if (!match && !userId && loaded) return <Redirect to="/auth/signin" />

  return null
}

export interface AuthInfo {
  userId?: number
  userRole?: 'admin' | 'regular' | 'operator'
  expiredIn?: number
  expireWarning?: boolean
}

export function useToken(): AuthInfo {
  const token = useRootSelector((state) => state.auth.token)
  const [authInfo, setAuthInfo] = useState(tokenToAuthInfo(token))

  useEffect(() => {
    const newAuth = tokenToAuthInfo(token)
    setAuthInfo(newAuth)

    if (newAuth.expiredIn) {
      const id = setTimeout(
        () => setAuthInfo((prev) => ({ ...prev, expireWarning: true })),
        newAuth.expiredIn - 120000,
      )
      return () => clearTimeout(id)
    }
  }, [token])

  return authInfo
}

function tokenToAuthInfo(token?: string): AuthInfo {
  if (!token) return {}

  try {
    const jwt = JSON.parse(atob(token.split('.')[1]))
    return {
      userId: parseInt(jwt.sub),
      userRole: jwt.role,
      expiredIn: Math.max(jwt.exp * 1000 - Date.now(), 0),
      expireWarning: false,
    }
  } catch {
    return {}
  }
}
// http://192.168.39.3:2103